import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import translate from '../../../commonComponents/translate';
import BasicPortlet from '../../../commonComponents/portlets/BasicPortlet';
import ProgressChart from './performance/ProgressChart';
import GameProp from '../propTypes/gameProp';
import BasicPortletWithFilters from '../../../commonComponents/portlets/BasicPortletWithFilters';
import AmColumnAndLineChart from '../../../commonComponents/charts/AmColumnAndLineChart';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import Notifications from '../../../services/notifications';
import Deviation from './performance/Deviation';
import Loader from '../../../services/loader';
import DateRange from '../../../commonComponents/forms/inputs/DateRange';
import Select from '../../../commonComponents/forms/inputs/Select';
import { gameStatus } from '../../../services/utilityFunctions';

// Gets and the unity type
const getUnity = (unity, t) => {
  let infoUnity = '';
  if (unity === 'points') {
    infoUnity = 'pts';
  } else if (unity === 'percentage') {
    infoUnity = '%';
  } else if (unity === 'VA') {
    infoUnity = t('challengeDetails_leaderboard_VA');
  }
  return infoUnity;
};

function Performance({ t, challenge, _csrf, user }) {
  // Check if challenge objective !== personal, if it is shows team deviation else hide it
  const isPersonal = challenge?.objective !== 'personal';
  // Loader for refresh data
  const loader = useRef(new Loader('#challenge-stats-container'));
  const [isLoading, setLoading] = useState(false);
  /** INITIAL STATE */
  const { loggedUser } = useContext(LoggedUserContext);
  const [performance, setPerformance] = useState([]);
  const [filterActive, setFilterActive] = useState({ user: 'week', team: 'week' });
  const challengeStatus = gameStatus(challenge.startDate, challenge.endDate);
  const isFinished = challengeStatus === 'finished';
  const initialStartDate = isFinished ? challenge.startDate : moment().utc().subtract(1, 'month').valueOf();
  const initialEndDate = isFinished ? challenge.endDate : moment().valueOf();
  const initialFiltersState = {
    team: {
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
    user: {
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
  };
  const [filters, setFilters] = useState(initialFiltersState);
  const handleDateChange = (startDate, endDate, type) => {
    setFilters(prevState => ({
      ...prevState,
      [type]: {
        startDate,
        endDate,
      },
    }));
  };

  // Graph filters (year, month, week)
  const graphFilterList = [
    {
      id: 'year',
      text: t('perform.performance.filter.groupPerYear'),
    },
    {
      id: 'month',
      text: t('perform.performance.filter.groupPerWeek'),
    },
    {
      id: 'week',
      text: t('perform.performance.filter.groupPerDay'),
    },
  ];

  /** API REQUESTS */
    // USER PERFORMANCE
  const getUserPerformance = () => {
      setLoading(true);
      const params = {
        user: user || loggedUser._id,
      };
      if (filters?.user?.startDate) {
        params.startDate = filters?.user?.startDate;
      }
      if (filters?.user?.endDate) {
        params.endDate = filters?.user?.endDate;
      }
      axios.get(`/api/game/${challenge._id}/stats/user`,
        {
          params,
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        })
        .then(({ data: response }) => {
          setPerformance(prevState => {
            const { unity } = response.data.target;
            const isCurrency = unity === 'currency';
            const infoUnity = getUnity(unity, t);
            const week = response.data.week.map(w => ({
              ...w,
              daySlashMonth: `${w.day}/${w.month}`,
              points: w.isHistoricalData ? null : (w.points),
              historicalPoints: w.isHistoricalData ? w.points : null,
              target: (w.target),
              unity: isCurrency ? w?.currencyInfo?.symbol || '' : (infoUnity || ''),
            }));
            const month = response.data.month.map(m => ({
              ...m,
              daySlashMonth: `${m.week}-${m.month}`,
              points: m.isHistoricalData ? null : (m.points),
              historicalPoints: m.isHistoricalData ? m.points : null,
              target: m.target,
              unity: isCurrency ? m?.currencyInfo?.symbol || '' : (infoUnity || ''),
            }));
            const year = response.data.year.map(y => ({
              ...y,
              monthString: moment().month(y.month - 1).format('MMM'),
              points: y.isHistoricalData ? null : (y.points),
              historicalPoints: y.isHistoricalData ? y.points : null,
              target: (y.target),
              unity: isCurrency ? y?.currencyInfo?.symbol || '' : (infoUnity || ''),
            }));

            setLoading(false);
            return { ...prevState, user: { ...response.data, year, month, week } };
          });
        })
        .catch(err => {
          console.error(err);
          Notifications.showErrorFromRequest(err, 'error_failed_getting_data');
        });
    };
  // TEAM PERFORMANCE
  const getTeamPerformance = () => {
    setLoading(true);
    const params = {};
    if (user || loggedUser._id) {
      params.user = user || loggedUser._id;
    }
    if (filters?.team?.startDate) {
      params.startDate = filters?.team?.startDate;
    }
    if (filters?.team?.endDate) {
      params.endDate = filters?.team?.endDate;
    }
    axios.get(`/api/game/${challenge._id}/stats/team`,
      {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        const { unity } = response.data.target;
        const isCurrency = unity === 'currency';
        const infoUnity = getUnity(unity, t);
        setPerformance(prevState => {
          const week = response.data.week.map(w => ({
            ...w,
            daySlashMonth: `${w.day}/${w.month}`,
            points: w.isHistoricalData ? null : w.points,
            userPoints: w.userPoints,
            historicalPoints: w.isHistoricalData ? w.points : null,
            userPointsAverage: w.userPointsAverage,
            unity: isCurrency ? w?.currencyInfo?.symbol || '' : (infoUnity || ''),
          }));
          const month = response.data.month.map(m => ({
            ...m,
            daySlashMonth: `${m.week}-${m.month}`,
            points: m.isHistoricalData ? null : m.points,
            userPoints: m.userPoints,
            historicalPoints: m.isHistoricalData ? m.points : null,
            userPointsAverage: m.userPointsAverage,
            unity: isCurrency ? m?.currencyInfo?.symbol || '' : (infoUnity || ''),
          }));
          const year = response.data.year.map(y => ({
            ...y,
            monthString: moment().month(y.month - 1).format('MMM'),
            points: y.isHistoricalData ? null : y.points,
            userPoints: y.userPoints,
            historicalPoints: y.isHistoricalData ? y.points : null,
            userPointsAverage: y.userPointsAverage,
            unity: isCurrency ? y?.currencyInfo?.symbol || '' : (infoUnity || ''),
          }));

          setLoading(false);
          return { ...prevState, team: { ...response.data, year, month, week } };
        });
      })
      .catch(err => {
        console.error(err);
        Notifications.showErrorFromRequest(err, 'error_failed_getting_data');
      });
  };

  // Render Charts
  const getCharts = type => {
    switch (type) {
      case 'progress':
        return (
          <ProgressChart
            target={performance?.user?.target}
            unityType={getUnity(performance?.user?.target?.unity, t)}
          />
        );
      case 'userDeviation':
        return (
          <Deviation
            titleDeviation={t('perform.performance.deviationBar.deviation')}
            titleTarget={t('perform.performance.deviationBar.target')}
            generalTitle={t('perform.performance.deviationBarUser.title')}
            points={performance?.user?.deviation?.points}
            target={performance?.user?.deviation?.target}
            targetObj={performance?.user?.target}
            deviation={performance?.user?.deviation?.deviation}
            deviationTarget={performance?.user?.deviation?.deviationTarget}
            unityType={getUnity(performance?.user?.target?.unity, t)}
          />
        );
      case 'userPerformanceChart':
        return (
          <AmColumnAndLineChart
            containerClass="report-chart-content"
            options={{
              category: filterActive.user === 'year' ? 'monthString' : 'daySlashMonth',
              yLabel: '', // Y TEXT,
              columnValueKey: 'historicalPoints',
              columnSeriesName: t('perform.performance.label.historical.scores'),
              column2ValueKey: 'points',
              column2SeriesName: t('perform.performance.label.scored'),
              lineValueKey: 'target',
              lineSeriesName: t('perform.performance.label.targeted'),
              colorRect: '#F5D509',
              colorRect2: '#B7DB5F',
              colorLine: '#FC5C1C',
              closerColumns: false,
              bullet: true,
              unity: performance?.user[filterActive.user][0]?.unity || '',
            }}
            type="layeredColumns"
            chartId={type}
            stats={performance?.user[filterActive.user]}
          />
        );
      case 'teamPerformanceChart':
        return (
          <AmColumnAndLineChart
            containerClass="report-chart-content"
            options={{
              category: filterActive.team === 'year' ? 'monthString' : 'daySlashMonth',
              yLabel: '', // Y TEXT,
              columnValueKey: 'points',
              columnSeriesName: t('perform.performance.label.teamScore'),
              column2ValueKey: 'userPoints',
              column2SeriesName: t('perform.performance.label.yourScore'),
              historicalColumnValueKey: 'historicalPoints',
              historicalColumnSeriesName: t('perform.performance.label.historical.scores'),
              lineValueKey: 'userPointsAverage',
              lineSeriesName: t('perform.performance.label.yourAverage'),
              colorRect: '#4FA8F4',
              colorRect2: '#B7DB5F',
              colorHistorical: '#F5D509',
              colorLine: '#FC5C1C',
              closerColumns: false,
              unity: performance?.team[filterActive.team][0]?.unity || '',
            }}
            chartId={type}
            stats={performance?.team[filterActive.team]}
            type="layeredColumns"
          />
        );
      case 'teamDeviation':
        return (
          <div className="deviation-team">
            <Deviation
              titleDeviation={t('perform.performance.deviationBar.deviation')}
              titleTarget={t('perform.performance.deviationBar.objective')}
              generalTitle=""
              points={performance?.team?.deviation?.points}
              target={performance?.team?.deviation?.target}
              targetObj={performance?.team?.deviation}
              deviation={performance?.team?.deviation?.deviation}
              deviationTarget={performance?.team?.deviation?.deviationTarget}
              unityType={getUnity(performance?.team?.deviation?.unity, t)}
            />
          </div>
        );
      default:
        return (
          <div className="report-chart-content" />
        );
    }
  };

  useEffect(() => {
    getUserPerformance();
    getTeamPerformance();
  }, [filters]);

  // Loader
  useEffect(
    () => {
      if (loader.current) {
        if (isLoading) {
          loader.current.show();
        } else {
          loader.current.hide();
        }
      }
    },
    [isLoading],
  );

  const renderFilter = type => {
    return (
      <div className="customHeadTools">
        <div className="select-container">
          <Select
            id={`dropdown-${type}`}
            items={graphFilterList}
            value={filterActive[type]}
            onChange={value => { setFilterActive(prevFilter => ({ ...prevFilter, [type]: value })); }}
            searchable={false}
          />
        </div>
        <DateRange
          id={`filterDate-${type}`}
          startDate={filters[type].startDate}
          endDate={filters[type].endDate}
          placeholder={t('commissions.filter_date_placeholder')}
          onChange={({ startDate, endDate }) => {
            handleDateChange(startDate, endDate, type);
          }}
          onClear={() => {
            handleDateChange(initialFiltersState[type].startDate, initialFiltersState[type].endDate, type);
          }}
          required={false}
        />
      </div>
    );
  };

  return (
    <div className="m-portlet__body" id="challenge-stats-container">
      <div className="row">
        <div className="col-md-6">
          {getCharts('progress')}
        </div>
        <div className="col-md-6 performance-deviation">
          {performance?.user ?
            getCharts('userDeviation')
            :
            <div className="game-charts" />
          }
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 col-12 your-performance-chart">
          <BasicPortletWithFilters
            title={t('perform.performance.graph.user.title')}
            customDropdown
            customFiltersChildren={renderFilter('user')}
          >
            {performance?.user ?
              getCharts('userPerformanceChart')
              :
              <div className="game-charts" />
            }
          </BasicPortletWithFilters>
        </div>
      </div>
      <div className="row">
        <div className={`${isPersonal ? 'col-md-6' : 'col-md-12'} col-12 team-performance-chart`}>
          <BasicPortletWithFilters
            title={t('perform.performance.graph.team.title')}
            customDropdown
            customFiltersChildren={renderFilter('team')}
          >
            {performance?.team ?
              getCharts('teamPerformanceChart')
              :
              <div className="game-charts" />
            }
          </BasicPortletWithFilters>
        </div>
        {isPersonal && (
          <div className="col-md-6 col-12 team-performance-chart team-deviation-performance">
            <BasicPortlet title={t('perform.performance.deviationBarTeam.title')}>
              {performance?.team ?
                getCharts('teamDeviation')
                :
                <div className="game-charts" />
              }
            </BasicPortlet>
          </div>
        )}
      </div>
    </div>
  );
}

Performance.propTypes = {
  t: PropTypes.func.isRequired,
  challenge: PropTypes.shape(GameProp.propType).isRequired,
  _csrf: PropTypes.string.isRequired,
  user: PropTypes.string,
};

Performance.defaultProps = {
  user: null,
};

export default translate(Performance);
